import { useCallback, useEffect, useRef } from 'react';

import { useRealtimeContext, useRealtimePublish } from '@feathr/hooks';

import type {
  FundraisingEventMap,
  IDonationCreatedEvent,
  IRawDonationSSEEvent,
} from './fundraisingEvents.types';
import { normalizeDonationEvent } from './fundraisingEvents.utils';

interface IUseFundraisingEventsOptions {
  /**
   * Handler called when a new donation is created.
   */
  onDonationCreated?: (donation: IDonationCreatedEvent) => void;
}

interface IUseFundraisingEventsReturn {
  /**
   * Publish a donation created event.
   * Used primarily by the debugger panel.
   */
  publishDonationCreated: (donation: IDonationCreatedEvent) => void;
}

/**
 * Hook for subscribing to and publishing fundraising events.
 *
 * @param options - Event handlers for different fundraising events
 * @returns Object with methods to publish events
 *
 * @example
 * ```tsx
 * function TransactionTable() {
 *   const [transactions, setTransactions] = useState<IDonationCreatedEvent[]>([]);
 *
 *   useFundraisingEvents({
 *     onDonationCreated: (donation) => {
 *       setTransactions((prev) => [donation, ...prev]);
 *     },
 *   });
 *
 *   return <Table>{...}</Table>;
 * }
 * ```
 */
function useFundraisingEvents(
  options: IUseFundraisingEventsOptions = {},
): IUseFundraisingEventsReturn {
  const { onDonationCreated } = options;
  const { publish } = useRealtimePublish<FundraisingEventMap>();
  const { eventBus } = useRealtimeContext<FundraisingEventMap>();

  // Use ref to always have latest callback without re-subscribing
  const onDonationCreatedRef = useRef(onDonationCreated);
  onDonationCreatedRef.current = onDonationCreated;

  useEffect(() => {
    // Handler receives either raw SSE events or already-transformed events (from mock mode)
    const handler = (rawEvent: IRawDonationSSEEvent | IDonationCreatedEvent): void => {
      const normalizedEvent = normalizeDonationEvent(rawEvent);
      onDonationCreatedRef.current?.(normalizedEvent);
    };

    const unsubscribe = eventBus.subscribe('fundraising.donation.created', handler);
    return unsubscribe;
  }, [eventBus]);

  const publishDonationCreated = useCallback(
    (donation: IDonationCreatedEvent): void => {
      publish('fundraising.donation.created', donation);
    },
    [publish],
  );

  return {
    publishDonationCreated,
  };
}

export default useFundraisingEvents;
export type { IUseFundraisingEventsOptions, IUseFundraisingEventsReturn };
